import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AuthLayout } from '../components';
import Homescreen_icon from '../../home/assets/Homescreen.png';
import whatsapp from '../assets/whatsapp.png';
import { getIntegrationStatus } from '../api';
import {
  AUTH_FORM_CARD_CLASS,
  AUTH_SUBTITLE_CLASS,
  AUTH_TITLE_CLASS,
} from '../lib/authStyles';

const FALLBACK_STEPS = [
  { key: 'pending', label: 'Account Pending' },
  { key: 'activating', label: 'Account Under Activation' },
  { key: 'activated', label: 'Account Activated' },
];

function CheckIcon({ className = 'w-4 h-4' }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );
}

function CrossIcon({ className = 'w-4 h-4' }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function buildDisplaySteps(apiSteps, status, apiStatus) {
  const isFullyDone = status === 'completed' || apiStatus === 'activated';
  const isFailed = status === 'cancelled' || apiStatus === 'failed';

  if (apiSteps?.length) {
    return apiSteps.map((step) => {
      const isCompleted = isFullyDone || !!step.is_completed;
      const isActive = !isFullyDone && !!step.is_current && !step.is_completed;
      return {
        label: step.label,
        isCompleted,
        isActive,
        isFailed: isFailed && isActive,
        isPending: !isCompleted && !isActive,
      };
    });
  }

  let activeIndex = 0;
  if (isFullyDone) activeIndex = FALLBACK_STEPS.length;
  else if (isFailed) activeIndex = 1;
  else if (apiStatus === 'activating' || apiStatus === 'in_progress')
    activeIndex = 1;
  else activeIndex = 0;

  return FALLBACK_STEPS.map((step, index) => {
    const isCompleted = isFullyDone || index < activeIndex;
    const isActive = !isFullyDone && index === activeIndex;
    return {
      label: step.label,
      isCompleted,
      isActive,
      isFailed: isFailed && isActive,
      isPending: !isCompleted && !isActive,
    };
  });
}

export function WhatsAppActivationPage() {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/login');
  };

  const hasCheckedOnMount = useRef(false);
  const [status, setStatus] = useState(() => {
    return localStorage.getItem('whatsapp_activation_status') || 'pending';
  });
  const [isConnected, setIsConnected] = useState(() => {
    const saved = localStorage.getItem('whatsapp_activation_is_connected');
    return saved ? JSON.parse(saved) : null;
  });
  const [apiStatusLabel, setApiStatusLabel] = useState(() => {
    return (
      localStorage.getItem('whatsapp_api_status_label') || 'Account Pending'
    );
  });
  const [apiStatus, setApiStatus] = useState(() => {
    return localStorage.getItem('whatsapp_api_status') || 'pending';
  });
  const [steps, setSteps] = useState(() => {
    const saved = localStorage.getItem('whatsapp_activation_steps');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('whatsapp_activation_status', status);
  }, [status]);

  useEffect(() => {
    if (isConnected !== null) {
      localStorage.setItem(
        'whatsapp_activation_is_connected',
        JSON.stringify(isConnected),
      );
    }
  }, [isConnected]);

  useEffect(() => {
    localStorage.setItem('whatsapp_api_status_label', apiStatusLabel);
    localStorage.setItem('whatsapp_api_status', apiStatus);
    if (steps && steps.length > 0) {
      localStorage.setItem('whatsapp_activation_steps', JSON.stringify(steps));
    }
  }, [apiStatusLabel, apiStatus, steps]);

  const companyId = localStorage.getItem('company_id');
  const integrationSlug =
    localStorage.getItem('selected_integration_slug') || 'whatsapp';

  const displaySteps = useMemo(
    () => buildDisplaySteps(steps, status, apiStatus),
    [steps, status, apiStatus],
  );

  const completedCount = displaySteps.filter((s) => s.isCompleted).length;
  const totalSteps = displaySteps.length || 3;
  const progressPercent = Math.min(
    100,
    Math.round((completedCount / totalSteps) * 100),
  );
  const hasActive = displaySteps.some((s) => s.isActive);
  const currentStepNumber = Math.min(
    totalSteps,
    Math.max(1, completedCount + (hasActive ? 1 : 0)),
  );

  useEffect(() => {
    if (
      !companyId ||
      ((status === 'completed' ||
        status === 'cancelled' ||
        isConnected === true) &&
        hasCheckedOnMount.current)
    )
      return;

    let pollInterval;

    const checkStatus = async () => {
      try {
        const data = await getIntegrationStatus({ companyId, integrationSlug });

        setApiStatus(data.status);
        if (data.status_label) setApiStatusLabel(data.status_label);
        if (data.steps) setSteps(data.steps);

        if (data.status === 'activated' || data.is_connected === true) {
          setIsConnected(true);
          if (status !== 'completed') {
            setStatus('completed');
            toast.success(
              'WhatsApp Business Integration activated successfully!',
            );
          }
        } else if (data.status === 'failed') {
          if (status !== 'cancelled') {
            setStatus('cancelled');
            toast.error('WhatsApp Business activation failed.');
          }
          setIsConnected(false);
        } else {
          setIsConnected(false);
          if (status !== 'pending') {
            setStatus('pending');
          }
        }
      } catch (error) {
        console.error('Error fetching activation status:', error);
      } finally {
        hasCheckedOnMount.current = true;
      }
    };

    checkStatus();

    if (
      status !== 'completed' &&
      status !== 'cancelled' &&
      isConnected !== true
    ) {
      pollInterval = setInterval(checkStatus, 300000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [status, companyId, integrationSlug, isConnected]);

  useEffect(() => {
    if (status === 'completed') {
      const redirectTimer = setTimeout(() => {
        navigate('/whatsapp-activated');
      }, 2000);
      return () => clearTimeout(redirectTimer);
    }
  }, [status, navigate]);

  return (
    <AuthLayout rightImage={Homescreen_icon} rightAlt="Aexon Assistant Mockup">
      <div className="animate-fadeIn">
        <div className="relative w-[90px] h-[90px] flex items-center justify-center mb-6 select-none">
          <div className="absolute inset-0 rounded-full bg-primary/5 shadow-[0_0_20px_rgba(80,171,61,0.08)] pointer-events-none" />
          <svg
            className="absolute inset-0 w-full h-full transform -rotate-90 text-primary"
            viewBox="0 0 90 90"
          >
            <circle
              cx="45"
              cy="45"
              r="41"
              fill="transparent"
              stroke="currentColor"
              strokeOpacity="0.15"
              strokeWidth="3"
            />
            <circle
              cx="45"
              cy="45"
              r="41"
              fill="transparent"
              stroke="currentColor"
              strokeWidth="3"
              strokeDasharray={2 * Math.PI * 41}
              strokeDashoffset={2 * Math.PI * 41 * (1 - progressPercent / 100)}
              strokeLinecap="round"
              className={`transition-all duration-1000 ease-out ${status === 'cancelled' ? 'text-status-error' : 'text-primary'}`}
            />
          </svg>
          <img
            src={whatsapp}
            alt=""
            className="w-12 h-12 object-contain relative z-10"
          />
        </div>

        <h1 className={AUTH_TITLE_CLASS}>
          {status === 'cancelled'
            ? 'Setup was cancelled'
            : 'Your account is under activation'}
        </h1>
        <h2 className={AUTH_SUBTITLE_CLASS}>
          {status === 'cancelled' ? (
            <>
              The integration configuration was interrupted. Please review
              credentials or try again.
            </>
          ) : (
            <>
              We&apos;re setting up your{' '}
              <span className="text-primary font-sf-pro-display">
                WhatsApp Business Pro
              </span>{' '}
              integration. This process may take up to 8-10 hours to complete.
            </>
          )}
        </h2>

        <div className={`${AUTH_FORM_CARD_CLASS} gap-6`}>
          <div className="flex flex-col gap-2.5">
            <div className="flex justify-between items-center text-body font-sf-pro-display">
              <span className="text-primary font-medium">
                Step {String(currentStepNumber).padStart(2, '0')} /{' '}
                {String(totalSteps).padStart(2, '0')}
              </span>
              <span className="text-text-secondary">
                {apiStatusLabel || 'In progress'}
              </span>
            </div>
            <div
              className="h-2 w-full overflow-hidden rounded-full bg-border-default"
              role="progressbar"
              aria-valuenow={progressPercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Activation ${progressPercent}% complete`}
            >
              <div
                className={`h-full transition-[width] duration-700 ease-out rounded-full ${
                  status === 'cancelled' ? 'bg-status-error' : 'bg-primary'
                }`}
                style={{
                  width: `${
                    status === 'cancelled'
                      ? Math.max(progressPercent, 33)
                      : Math.max(progressPercent, 8)
                  }%`,
                }}
              />
            </div>
          </div>

          <ol className="flex flex-col">
            {displaySteps.map((step, index) => {
              const isLast = index === displaySteps.length - 1;

              return (
                <li
                  key={`${step.label}-${index}`}
                  className="relative flex gap-4"
                >
                  <div className="relative flex flex-col items-center shrink-0 w-8">
                    <div
                      className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors duration-500 ${
                        step.isCompleted
                          ? 'bg-primary border-primary text-surface-mint'
                          : step.isFailed
                            ? 'bg-status-error/10 border-status-error text-status-error'
                            : step.isActive
                              ? 'bg-primary/10 border-primary text-primary'
                              : 'bg-input-background border-border-default text-text-muted'
                      }`}
                    >
                      {step.isCompleted ? (
                        <CheckIcon />
                      ) : step.isFailed ? (
                        <CrossIcon />
                      ) : step.isActive ? (
                        <span className="w-3.5 h-3.5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                      ) : (
                        <span className="text-body-sm font-medium font-sf-pro-display">
                          {index + 1}
                        </span>
                      )}
                    </div>
                    {!isLast && (
                      <div
                        className={`w-0.5 flex-1 min-h-[28px] my-1 rounded-full transition-colors duration-500 ${
                          step.isCompleted ? 'bg-primary' : 'bg-border-default'
                        }`}
                      />
                    )}
                  </div>

                  <div
                    className={`flex flex-col justify-center ${isLast ? 'pb-1' : 'pb-7'}`}
                  >
                    <span
                      className={`text-h6-medium font-sf-pro-display transition-colors duration-300 ${
                        step.isCompleted || step.isActive
                          ? 'text-text-primary'
                          : 'text-text-muted'
                      }`}
                    >
                      {step.label}
                    </span>
                    {step.isActive && !step.isFailed && (
                      <span className="text-body font-sf-pro-display text-text-secondary mt-0.5">
                        In progress…
                      </span>
                    )}
                    {step.isFailed && (
                      <span className="text-body font-sf-pro-display text-status-error mt-0.5">
                        Activation failed
                      </span>
                    )}
                    {step.isCompleted && (
                      <span className="text-body font-sf-pro-display text-primary mt-0.5">
                        Completed
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="text-center mt-2">
            <button
              type="button"
              onClick={handleBack}
              className="text-body-lg font-normal font-sf-pro-display text-text-primary hover:text-primary transition-colors cursor-pointer inline-flex items-center gap-1.5"
            >
              <svg
                className="w-4 h-4 inline"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                />
              </svg>
              <span>Back to Login</span>
            </button>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
